// Lista de contratos

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TextField,
  Tooltip,
  IconButton,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from "@mui/icons-material/Edit";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getServiceTypeLabel } from './ServiceType';
import { apiFetch, apiFetchJson } from '../fetchWrapper';
import { formatMX, formatMXDateTimeNoSeconds } from '../util/DateUtil';
import ContractForm from './ContractForm';


const ContractList = ({ onSelect }) => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderBy, setOrderBy] = useState('status');
  const [order, setOrder] = useState('asc');
  const [search, setSearch] = useState('');
  const [showContractForm, setShowContractForm] = useState(false);
  const [editingContract, setEditingContract] = useState(null);

  const handleAdd = async () => {
    setShowContractForm(true);
    setEditingContract(null);
  }

  const handleCancelForm = () => {
    setShowContractForm(false);
    setEditingContract(null);
  }

  const getClientName = (c) => {
    if (c.clientId) {
      const client = clients.find(cl => cl.id === c.clientId);
      return client ? client.companyName : 'Desconocido';
    }
    return c.organizationName;
  };

  const filteredContracts = contracts.filter(c => {
    const searchLower = search.toLowerCase();

    const clientName = getClientName(c)?.toLowerCase() || '';
    const organization = c.organizationName?.toLowerCase() || '';
    const description = c.description?.toLowerCase() || '';

    return (
      clientName.includes(searchLower) ||
      organization.includes(searchLower) ||
      description.includes(searchLower)
    );
  });

  const sortedContracts = [...filteredContracts].sort((a, b) => {
    let valA;
    let valB;

    switch (orderBy) {
      case 'createdAt':
        valA = new Date(a.createdAt);
        valB = new Date(b.createdAt);
        break;
      case 'updatedAt':
        valA = new Date(a.updatedAt);
        valB = new Date(b.updatedAt);
        break;
      case 'organizationName':
        valA = a.organizationName || '';
        valB = b.organizationName || '';
        break;
      default:
        valA = a[orderBy];
        valB = b[orderBy];
    }

    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    setLoading(true);

    Promise.all([
      apiFetchJson('/api/contracts'),
      apiFetchJson('/api/clients')
    ])
      .then(([contractsData, clientsData]) => {
        setContracts(contractsData);
        setClients(clientsData);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const handleExport = async () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Listado de Cotizaciones", 14, 22);

    // Prepare table data
    const tableColumn = ["Nombre De La Organización", "Fecha de creación", "Fecha de actualización", "Elementos", "Tipo de servicio", "Descripción"];
    const tableRows = sortedContracts.map((c) => [
      c.organizationName || '-',
      new Date(c.createdAt).toLocaleString(),
      new Date(c.updatedAt).toLocaleString(),
      c.elementsNumber || '-',
      getServiceTypeLabel(c.serviceType) || '-',
      c.description || '-',
    ]);

    autoTable(doc, {
      startY: 30, // start below title
      head: [tableColumn],
      body: tableRows,
      theme: "striped",
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      styles: { fontSize: 10 },
    });

    // guardar PDF
    doc.save("contratos.pdf");
  }

  const handleEdit = async (contract) => {
    setEditingContract(contract);
    setShowContractForm(true);
  }

  const handleDelete = async (id) => {
    const confirmed = window.confirm('¿Desea eliminar este contrato?');
    if (!confirmed) return;

    try {
      const res = await apiFetch(`/api/contracts/${id}`, {
        method: 'DELETE',
      });

      if (!res) return;

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setContracts(contracts.filter(c => c.id !== id));
      alert('Contrato eliminado');
    } catch (err) {
      console.error(err);
      alert('Error al eliminar el contrato');
    }
  };

  const handleSaveContract = (savedContract) => {
    setContracts((prev) => {
      const exists = prev.some((c) => c.id === savedContract.id);

      if (exists) {
        // Actualizar
        return prev.map((c) =>
          c.id === savedContract.id ? savedContract : c
        );
      }

      // Crear
      return [savedContract, ...prev];
    });

    setShowContractForm(false);
    setEditingContract(null);
  };

  if (loading) return <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 4 }} />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>{showContractForm && (
      <>
        <ContractForm contract={editingContract} onBack={handleCancelForm} onSave={handleSaveContract} />
        <br />
      </>
    )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Contratos</Typography>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          fontSize: { xs: 10, sm: 12 },
          px: 1,
        }}>
          <Button sx={{
            backgroundColor: "#3f74b1",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#364f6b"
            }
          }}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}>
            Agregar Contrato
          </Button>

          <Button
            variant="outlined"
            color="primary"
            startIcon={<DownloadIcon />}
            onClick={handleExport}>
            Exportar
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ p: 2 }}>
        <TextField
          label="Buscar contrato"
          variant="outlined"
          size="small"
          fullWidth
          sx={{ mb: 2 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <Table>
          <TableHead
            sx={{
              backgroundColor: '#f5f5f5',
              textTransform: 'uppercase',
              width: "100%",
            }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'organizationName'}
                  direction={orderBy === 'organizationName' ? order : 'asc'}
                  onClick={() => handleSort('organizationName')}>
                  Cliente
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleSort('createdAt')}>
                  Fecha de creación
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleSort('updatedAt')}>
                  Última actualización
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>Tipo de servicio</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>No. elementos </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>No. supervisores</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Descripción</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'startDate'}
                  direction={orderBy === 'startDate' ? order : 'asc'}
                  onClick={() => handleSort('startDate')}>
                  Fecha de inicio
                </TableSortLabel>
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>
                <TableSortLabel
                  active={orderBy === 'endDate'}
                  direction={orderBy === 'endDate' ? order : 'asc'}
                  onClick={() => handleSort('endDate')}>
                  Fecha de finalización
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>+ Info</TableCell>

            </TableRow>
          </TableHead>

          <TableBody>
            {sortedContracts.map((c) => (
              <TableRow
                key={c.id}
                hover
                sx={{
                  "&:hover .MuiTableCell-root": {
                    backgroundColor: "action.hover",
                  },
                }}
              >
                <TableCell>{getClientName(c)}</TableCell>
                <TableCell>{formatMXDateTimeNoSeconds(c.createdAt)}</TableCell>
                <TableCell>{formatMXDateTimeNoSeconds(c.updatedAt)}</TableCell>
                <TableCell>{getServiceTypeLabel(c.serviceType)}</TableCell>
                <TableCell>{c.elementsNumber}</TableCell>
                <TableCell>{c.supervisorsNumber}</TableCell>
                <TableCell>{c.description}</TableCell>
                <TableCell>{formatMX(c.startDate)}</TableCell>
                <TableCell>{formatMX(c.endDate)}</TableCell>

                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      mr: 1,
                      backgroundColor: "#3f74b1",
                      color: "#fff",
                      "&:hover": {
                        backgroundColor: "#364f6b"
                      }
                    }}
                    onClick={() => onSelect(c)}>
                    Ver detalle
                  </Button>
                  <Tooltip title="Editar"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px'
                        }
                      }
                    }}
                  >
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(c)}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Borrar"
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px'
                        }
                      }
                    }}
                  >
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(c.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ContractList;
